import { WarningCircle } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import { Button } from 'app/components/ui/button'
import type { ContactJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { debug, logError } from 'lib/logging'

export function RevertNonPatient({
  contact,
  onChange,
}: {
  contact: ContactJson
  onChange: (contact: ContactJson) => void
}): ReactElement | null {
  const contactStore = useContactStore()
  const [saving, setSaving] = useState<boolean>(false)
  const handleRevertMarkNonPatient = async () => {
    setSaving(true)
    try {
      const updateResult = await contactStore.update(contact.id!, { patientStatus: `Active` })
      debug(`contacts`, `update patient status`, updateResult)
      if (updateResult.error) {
        logError(`revert mark non patient failed - patientStatus`, updateResult.error.message)
        notifyError(`There was an error updating the contact`)
        return
      }
      const rebuildResult = await contactStore.recalculateContact(contact.id!)
      debug(`contacts`, `revert mark non patient rebuild`, rebuildResult)
      if (rebuildResult.error) {
        logError(`revert mark non patient - rebuild`, rebuildResult.error.message)
        notifyError(`There was an error rebuilding the contact`)
        await contactStore.update(contact.id!, { patientStatus: `NonPatient` })
        return
      }
      const recalculated = (rebuildResult.data as any)?.recalculateContact as ContactJson | undefined
      if (recalculated?.patientStatus === `NonPatient`) {
        logError(`revert mark non patient - rebuild restored NonPatient`, contact.id)
        notifyError(`The rebuild marked this contact as a non-patient again`)
        onChange(recalculated)
        return
      }
      notifySuccess(`Revert Non Patient Completed`)
      onChange(recalculated ?? ({ ...contact, patientStatus: `Active` } as ContactJson))
    } catch (err) {
      logError(`rebuild failed`, err)
      notifyError(`There was an error marking a contact as patient`)
    } finally {
      setSaving(false)
    }
  }
  if (contact.patientStatus !== `NonPatient` && !saving) return null
  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-3 rounded-md border border-danger-subtle bg-danger-minimal px-4 py-3"
    >
      <WarningCircle className="size-5 shrink-0 text-danger-default" />
      <p className="min-w-0 flex-1 text-body-sm text-danger-strong">Contact marked as a non-patient.</p>
      <Button variant="white" size="xs" loading={saving} onClick={handleRevertMarkNonPatient}>
        Revert
      </Button>
    </div>
  )
}
